import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import StarRating from "../components/ui/StarRating";
import { useLanguage } from "../contexts/LanguageContext";
import {
  formatCurrency,
  getCoursePrice,
  getCourseTitle,
  getMyCourses,
  submitCourseReview,
} from "../api/courses";
import logger from "../utils/logger.js";

function createReviewDraft() {
  return {
    rating: "5",
    comment: "",
  };
}

export default function MyCourses({ embedded = false }) {
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [actionCourseId, setActionCourseId] = useState(0);
  const [reviewDrafts, setReviewDrafts] = useState({});

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError("");
        setNotice("");
        const list = await getMyCourses();
        if (active) {
          setCourses(list);
        }
      } catch (err) {
        logger.error("My courses error", err);
        if (active) {
          setError(isAr ? "تعذر تحميل كورساتك." : "Unable to load your courses.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      active = false;
    };
  }, [isAr]);

  function updateReviewDraft(enrollmentId, field, value) {
    setReviewDrafts((current) => ({
      ...current,
      [enrollmentId]: {
        ...(current[enrollmentId] || createReviewDraft()),
        [field]: value,
      },
    }));
  }

  async function handleSubmitReview(item) {
    const draft = reviewDrafts[item.id] || createReviewDraft();

    try {
      setActionCourseId(item.id);
      setError("");
      setNotice("");

      const review = await submitCourseReview(item.course.id, {
        rating: Number(draft.rating || 5),
        comment: draft.comment.trim() || undefined,
      });

      setCourses((current) =>
        current.map((entry) =>
          entry.id === item.id
            ? {
                ...entry,
                can_review: false,
                review,
                course: {
                  ...entry.course,
                  reviews_count: Number(entry.course?.reviews_count || 0) + 1,
                  average_rating:
                    Number(entry.course?.reviews_count || 0) > 0
                      ? Number((
                          ((Number(entry.course?.average_rating || 0) *
                            Number(entry.course?.reviews_count || 0)) +
                            Number(review?.rating || 0)) /
                          (Number(entry.course?.reviews_count || 0) + 1)
                        ).toFixed(2))
                      : Number(review?.rating || 0),
                },
              }
            : entry,
        ),
      );
      setReviewDrafts((current) => ({ ...current, [item.id]: createReviewDraft() }));
      setNotice(isAr ? "تم إرسال تقييم الكورس بنجاح." : "Course review submitted successfully.");
    } catch (err) {
      logger.error("Course review error", err);
      setError(err?.message || (isAr ? "تعذر إرسال تقييم الكورس." : "Unable to submit the course review."));
    } finally {
      setActionCourseId(0);
    }
  }

  return (
    <div className={embedded ? "" : "container py-4 py-lg-5"} dir={isAr ? "rtl" : "ltr"}>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <h1 className={`${embedded ? "h3" : ""} fw-bold mb-1`}>
            {isAr ? "كورساتي" : "My Courses"}
          </h1>
          <p className="text-muted mb-0">
            {isAr
              ? "كل الكورسات التي سجلت فيها ونسبة التقدم بكل واحد."
              : "All courses you enrolled in and the progress for each one."}
          </p>
        </div>
        <Link to="/courses" className="btn btn-outline-dark rounded-pill px-4">
          {isAr ? "تصفح المزيد" : "Browse More"}
        </Link>
      </div>

      {notice ? <div className="alert alert-success rounded-4">{notice}</div> : null}

      {loading ? (
        <div className="py-5 text-center">
          <div className="spinner-border text-primary mb-3" role="status" />
          <p className="mb-0">{isAr ? "جارِ تحميل كورساتك..." : "Loading your courses..."}</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger rounded-4">{error}</div>
      ) : courses.length === 0 ? (
        <div className="card border-0 shadow-sm rounded-4">
          <div className="card-body py-5 text-center">
            <h2 className="h4 fw-bold mb-2">
              {isAr ? "لا توجد كورسات مسجل بها بعد" : "You are not enrolled in any courses yet"}
            </h2>
            <p className="text-muted mb-4">
              {isAr
                ? "ابدأ من صفحة الكورسات واختر الكورس المناسب لك."
                : "Start from the courses page and choose the right one for you."}
            </p>
            <Link to="/courses" className="btn btn-primary rounded-pill px-4">
              {isAr ? "عرض الكورسات" : "View Courses"}
            </Link>
          </div>
        </div>
      ) : (
        <div className="row g-4">
          {courses.map((item) => {
            const course = item.course || {};
            const progress = Number(item.progress_percent || course?.enrollment?.progress_percent || 0);
            const reviewDraft = reviewDrafts[item.id] || createReviewDraft();
            const review = item.review || null;

            return (
              <div className="col-12 col-lg-6" key={item.id}>
                <div className="card border-0 shadow-sm rounded-4 h-100 overflow-hidden">
                  <div className="row g-0 h-100">
                    <div className="col-md-4">
                      <img
                        src={
                          course?.thumbnail ||
                          "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=900&q=80"
                        }
                        alt={getCourseTitle(course, isAr)}
                        className="course-my-card__image"
                      />
                    </div>
                    <div className="col-md-8">
                      <div className="card-body h-100 d-flex flex-column">
                        <div className="d-flex flex-wrap gap-2 mb-2">
                          <span className="badge text-bg-light">{course?.level || "beginner"}</span>
                          <span className={`badge ${course?.is_free ? "text-bg-success" : "text-bg-dark"}`}>
                            {course?.is_free
                              ? isAr
                                ? "مجاني"
                                : "Free"
                              : formatCurrency(getCoursePrice(course))}
                          </span>
                        </div>

                        <h2 className="h5 fw-bold mb-2">{getCourseTitle(course, isAr)}</h2>
                        <p className="text-muted small mb-3">
                          {course?.duration_text || (isAr ? "مرن" : "Flexible duration")}
                        </p>

                        <div className="mt-auto">
                          <div className="d-flex flex-wrap align-items-center gap-3 mb-3 small text-muted">
                            <StarRating value={course?.average_rating || 0} size={16} showValue />
                            <span>
                              {Number(course?.reviews_count || 0)} {isAr ? "تقييم منشور" : "published reviews"}
                            </span>
                          </div>

                          <div className="d-flex justify-content-between small mb-2">
                            <span>{isAr ? "التقدم" : "Progress"}</span>
                            <strong>{progress}%</strong>
                          </div>
                          <div className="progress rounded-pill mb-3" style={{ height: 10 }}>
                            <div
                              className="progress-bar"
                              role="progressbar"
                              style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
                              aria-valuenow={progress}
                              aria-valuemin="0"
                              aria-valuemax="100"
                            />
                          </div>

                          <div className="d-flex flex-wrap gap-2">
                            <Link
                              to={`/learn/${course?.slug || course?.id}`}
                              className="btn btn-primary rounded-pill px-4"
                            >
                              {progress > 0
                                ? isAr
                                  ? "متابعة"
                                  : "Continue"
                                : isAr
                                  ? "ابدأ التعلم"
                                  : "Start Learning"}
                            </Link>
                            <Link
                              to={`/courses/${course?.slug || course?.id}`}
                              className="btn btn-outline-dark rounded-pill"
                            >
                              {isAr ? "تفاصيل الكورس" : "Course Details"}
                            </Link>
                          </div>

                          <div className="border rounded-4 p-3 mt-3 bg-light-subtle">
                            <h3 className="h6 fw-bold mb-2">{isAr ? "التقييم" : "Review"}</h3>
                            {review ? (
                              <div className="d-grid gap-2">
                                <StarRating value={review.rating || 0} size={18} showValue />
                                <div className="text-muted small">
                                  {review.comment || (isAr ? "بدون تعليق إضافي." : "No additional comment.")}
                                </div>
                              </div>
                            ) : item.can_review ? (
                              <div className="d-grid gap-2">
                                <select
                                  className="form-select"
                                  value={reviewDraft.rating}
                                  onChange={(event) => updateReviewDraft(item.id, "rating", event.target.value)}
                                  disabled={actionCourseId === item.id}
                                >
                                  {[5, 4, 3, 2, 1].map((rating) => (
                                    <option key={rating} value={rating}>
                                      {rating}/5
                                    </option>
                                  ))}
                                </select>
                                <textarea
                                  className="form-control"
                                  rows="3"
                                  value={reviewDraft.comment}
                                  onChange={(event) => updateReviewDraft(item.id, "comment", event.target.value)}
                                  placeholder={isAr ? "اكتب تعليقك عن الكورس" : "Write your course review"}
                                  disabled={actionCourseId === item.id}
                                />
                                <button
                                  type="button"
                                  className="btn btn-dark rounded-pill"
                                  onClick={() => handleSubmitReview(item)}
                                  disabled={actionCourseId === item.id}
                                >
                                  {actionCourseId === item.id
                                    ? isAr
                                      ? "جارٍ الإرسال..."
                                      : "Submitting..."
                                    : isAr
                                      ? "إرسال التقييم"
                                      : "Submit Review"}
                                </button>
                              </div>
                            ) : (
                              <div className="text-muted small">
                                {isAr
                                  ? "يمكنك إضافة تقييم بعد إكمال الكورس."
                                  : "You can add a review after completing the course."}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
